// Three-way theme control for the side menu: System / Light / Dark.
//
// "System" is the default and is a real option rather than an implicit state —
// a teacher whose phone flips to dark at sunset expects the app to follow, and
// without an explicit control there is no way back once they've tapped either
// of the other two.

import React, { useEffect, useState } from "react";
import { Monitor, Sun, Moon } from "lucide-react";
import {
  getThemeSetting, setThemeSetting, watchSystemTheme,
  type ThemeSetting,
} from "../services/theme";

const OPTIONS: Array<{ value: ThemeSetting; label: string; icon: React.ReactNode }> = [
  { value: "system", label: "System", icon: <Monitor size={13} /> },
  { value: "light",  label: "Light",  icon: <Sun size={13} /> },
  { value: "dark",   label: "Dark",   icon: <Moon size={13} /> },
];

export default function ThemeToggle() {
  const [setting, setSetting] = useState<ThemeSetting>(() => getThemeSetting());

  // Re-render when the OS flips while we're on "system", so the segmented
  // control's own colours track the change it just caused.
  useEffect(() => watchSystemTheme(() => setSetting(getThemeSetting())), []);

  function pick(v: ThemeSetting) {
    setThemeSetting(v);
    setSetting(v);
  }

  return (
    <div className="px-3 py-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">
        Appearance
      </div>
      <div role="radiogroup" aria-label="Appearance"
        className="grid grid-cols-3 gap-1 p-1 rounded-lg bg-white/[0.04] border border-white/10">
        {OPTIONS.map((o) => {
          const active = setting === o.value;
          return (
            <button key={o.value} type="button" role="radio" aria-checked={active}
              onClick={() => pick(o.value)}
              className={`flex items-center justify-center gap-1.5 py-2 rounded-md text-[11px] transition-colors ${
                active
                  ? "bg-cobalt-500 text-oncolor font-semibold"
                  : "text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]"
              }`}>
              {o.icon} {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
